import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import { faBan, faChevronRight, faPlusCircle } from "@fortawesome/pro-regular-svg-icons";
import { IAPIResponse } from "api/protocols";
import { INotification } from "api/rpc/2024-04/customer/teesheet";
import classNames from "classnames";
import Callout from "components/callout/Callout";
import LoaderCard from "components/loaderCard/LoaderCard";
import { convertTime } from "helpers/Helpers";
import { MOBILE_WIDTH } from "helpers/ScreenSizes";
import { useAppSelector } from "hooks/redux";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { groupBy } from "lodash";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { IBookingParticipant, ISlot, ITeeTime } from "redux/reducers/models/teetime";
import "./clientPortalTeeSheet.scss";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

interface IProps {
  checkTeeTime: (date: string, teeTimeId: ITeeTime, slot?: ISlot) => Promise<void>;
  teeTimeRes: IAPIResponse<Array<ITeeTime>>;
  notifications: Array<INotification>;
  showRestrictedBookings: boolean;
}

type TSlotGroup = Array<Array<ISlot>>;

interface IState {
  teeTimes: Array<ITeeTime & { slotGroups: TSlotGroup }>;
}

export default function ClientPortalTeeSheet(props: IProps) {
  const { checkTeeTime, teeTimeRes, notifications, showRestrictedBookings } = props;
  const {t} = useTranslation();
  const windowSize = useWindowSize();
  const { clientPortalStore } = useAppSelector(store => store);
  const [state, setState] = useState<IState>({
    teeTimes: null,
  });

  useEffect(() => {
    if (teeTimeRes) {
      const teeTimes = teeTimeRes?.data;
      let updatedTeeTimes = teeTimeRes?.data?.map((teetime, rowIndex) => {
        return {
          ...teetime,
          slotGroups: Object.values(
            groupBy(teeTimes[rowIndex].slots, s => (s.tee_time_booking_id === null ? s.id : s.tee_time_booking_id)),
          ),
        };
      });
      if (!showRestrictedBookings) {
        updatedTeeTimes = updatedTeeTimes?.filter(teetime => !teetime?.is_restricted);
      }
      setState(prevState => ({ ...prevState, teeTimes: updatedTeeTimes }));
    } else {
      setState(prevState => ({ ...prevState, teeTimes: null }));
    }
  }, [teeTimeRes, showRestrictedBookings]);

  const teeTimes = useMemo(() => {
    if (state?.teeTimes === null) {
      return [...Array(12)]?.map((num, index) => (
        <div key={index} className="client-portal-slots-container">
          <p className="client-portal-slots-start-time"></p>
          {[...Array(4)]?.map((card, cardIndex) => (
            <LoaderCard key={cardIndex} customCSSProperties={{ width: "100%", height: "40px" }} />
          ))}
        </div>
      ));
    } else if ((state?.teeTimes && state?.teeTimes?.length === 0) || state?.teeTimes === undefined) {
      return <Callout title={t("No Tee Times")} content={t("There are no tee times for this day")} type="error" />;
    } else {
      return state?.teeTimes?.map((teeTime, index, teeTimes) => {
        const prevTime = teeTimes[index - 1] ? moment(teeTimes[index - 1]?.start_time, "HH:mm").format("hh") : null;
        const currentTime = moment(teeTime?.start_time, "HH:mm").format("hh");
        const prevTeeTime = teeTimes[index - 1];
        const nextTeeTime = teeTimes[index + 1];
        const eventTeeTime = teeTime?.blocked_type === "shotgun" || teeTime?.blocked_type === "shotgun_crossover";
        return (
          <React.Fragment key={index}>
            {windowSize.width > MOBILE_WIDTH && (index === 0 || prevTime !== currentTime) ? (
              <div className="client-portal-slots-hour-block">
                <p className="client-portal-slots-hour">
                  {moment(teeTime?.start_time?.split(":")[0] + ":00", "HH:mm").format("h:mm A")}
                </p>
                <div className="client-portal-slots-hour-line"></div>
              </div>
            ) : null}
            {eventTeeTime &&
              prevTeeTime?.blocked_type !== "shotgun" &&
              prevTeeTime?.blocked_type !== "shotgun_crossover" && (
                <div className="client-portal-slots-container client-portal-slots-container-event-header">
                  Shotgun - {convertTime(teeTime?.start_time)}
                </div>
              )}
            <div
              className={classNames("client-portal-slots-container", {
                "client-portal-slots-container-event": eventTeeTime,
                // "client-portal-slots-container-event-first": eventTeeTime && (prevTeeTime === undefined
                //    || prevTeeTime?.start_time !== teeTime?.start_time),
                "client-portal-slots-container-event-last":
                  eventTeeTime && (nextTeeTime === undefined || nextTeeTime?.start_time !== teeTime?.start_time),
              })}
            >
              <p className="client-portal-slots-start-time">
                {teeTime?.blocked_type === "shotgun" || teeTime?.blocked_type === "shotgun_crossover"
                  ? teeTime?.start_hole
                  : convertTime(teeTime.start_time)}
              </p>
              <div className="client-portal-slots-group-full-width">
                <div className="client-portal-slots-group-parent-container">
                  {teeTime?.slotGroups?.map((slotGroup, slotGroupIndex) => {
                    return (
                      <div
                        key={slotGroupIndex}
                        className={classNames(
                          "client-portal-slots-group-container w-full",
                          `client-portal-tee-time-slot_spaced_${slotGroup?.length}`,
                          { "client-portal-tee-time-slot_last": teeTime?.slotGroups?.length === slotGroupIndex + 1 },
                        )}
                      >
                        {slotGroup?.map(
                          (
                            slot: ISlot & {
                              booking_participant: IBookingParticipant & { default_booking_category_title: string };
                            },
                            slotIndex,
                          ) => {
                            const participantName = slot?.booking_participant?.guest
                              ? "Guest"
                              : slot.booking_participant?.full_name && slot.booking_participant?.full_name?.trim()
                              ? slot?.booking_participant?.full_name
                              : slot?.booking_participant?.default_booking_category_title
                              ? slot?.booking_participant?.default_booking_category_title
                              : "Golfer";
                            if (slot?.tee_time_booking_participant_id) {
                              if (windowSize.width <= MOBILE_WIDTH) {
                                return (
                                  <div
                                    key={slotIndex}
                                    className={classNames(
                                      "client-portal-slots-single-slot client-portal-slots-participant",
                                      {
                                        "client-portal-slots-disabled": !!slot?.tee_time_booking_participant_id,
                                      },
                                    )}
                                  >
                                    <p className="client-portal-slots-single-slot-name">{participantName}</p>
                                    {participantName !== slot?.booking_participant?.default_booking_category_title && (
                                      <p className="client-portal-slots-single-slot-booking-category">
                                        {slot?.booking_participant?.default_booking_category_title}
                                      </p>
                                    )}
                                    <p className="client-portal-slots-single-slot-holes">{slot?.booking?.holes}</p>
                                  </div>
                                );
                              } else {
                                return (
                                  <div
                                    key={slotIndex}
                                    className={classNames(
                                      "client-portal-slots-single-slot client-portal-slots-participant",
                                      {
                                        "client-portal-slots-disabled": !!slot?.tee_time_booking_participant_id,
                                      },
                                    )}
                                  >
                                    <p className="client-portal-slots-single-slot-holes">{slot?.booking?.holes}</p>
                                    <div style={{ display: "flex", flexDirection: "column", minWidth: "0" }}>
                                      <p className="client-portal-slots-single-slot-name">{participantName}</p>
                                      {participantName !==
                                        slot?.booking_participant?.default_booking_category_title && (
                                        <p className="client-portal-slots-single-slot-booking-category">
                                          {slot?.booking_participant?.default_booking_category_title}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                );
                              }
                            } else {
                              //Block tee time if only 1 slot is available and single bookings are not allowed
                              const blockTeeTime =
                                teeTime?.quantity_remaining === 1 &&
                                clientPortalStore?.teeSheetSettings?.single_bookings === "none";
                              return (
                                <div
                                  key={slotIndex}
                                  className={classNames("client-portal-slots-single-slot", {
                                    "client-portal-slots-disabled": blockTeeTime,
                                    "client-portal-slots-restricted": teeTime?.is_restricted,
                                  })}
                                  onClick={() =>
                                    !blockTeeTime ? checkTeeTime(teeTime.date, teeTime, slot) : undefined
                                  }
                                >
                                  {teeTime?.is_restricted ? (
                                    <OverlayTrigger
                                    placement="auto"
                                    overlay={<Tooltip id="Restricted-booking"><>{t("Restricted tee time")} <br/> {t("Your member rate is not valid at this time. Regular green fee applies.")}</></Tooltip>}
                                  ><span><p className="client-portal-slots-restricted-text">Restricted booking <FontAwesomeIcon icon={faChevronRight} /></p></span></OverlayTrigger>
                                  ) : (
                                    <FontAwesomeIcon
                                    icon={blockTeeTime ? faBan : faPlusCircle}
                                    size="1x"
                                    className="client-portal-slots-single-slot-add"
                                  />
                                  )}
                                 
                                </div>
                              );
                            }
                          },
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </React.Fragment>
        );
      });
    }
  }, [state.teeTimes]);

  return (
    <div className="client-portal-tee-sheet">
      <div>
        {notifications?.map((notification, index) => (
          <Callout key={index} type="info" title={notification.subject} content={notification.content} />
        ))}
      </div>
      {teeTimes}
    </div>
  );
}
